import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { useTranslation } from "../i18n";
import { RAD, FS, Z } from "../lib/tokens";
import { ModalLayout } from "./SharedUI";
import { useTaxonomy } from "../hooks/useTaxonomy";
import { m365ImportTypesFromEntries } from "../utils/taxonomyMap";
import { outcomeVariant } from "../utils"; // 166 §G.3
import { api } from "../api"; // 174 §1.2 — the one door, which sends BOTH credentials
import Table from "./Table";

// Phase M+5 — Outlook calendar one-way import.
// Pulls the user's calendar from /me/calendarView, classifies each event
// against AgendaEvent.outlookEventId, and lets the user confirm a batch
// import. NEW rows default-checked; ALREADY rows hidden by default
// (toggle to show greyed-out and non-selectable). Per-row case picker
// pre-fills from a subject parse (the firm's case-ref pattern plus any
// legacy ones — FirmSettings.caseRefPattern / caseRefLegacyPatterns, P20d;
// seeded YYYY/NNN and YYYYWAT) when the parsed ref resolves to an existing
// case. The parse itself is server-side. A subject naming a COLLECTIVE case
// (FirmSettings.ccRefPattern, seeded CC-YYYY/NNN) no longer pre-fills the case
// that happens to carry the same number — the parser names the ledger now, so
// the row arrives with an empty picker and `suggestedCcRef` telling the user
// which collective case it is about. Per-row activity type defaults to
// whichever event type the taxonomy tags meta.m365ImportRole 'virtual' for
// online meetings (isOnlineMeeting=true) and 'inPerson' for physical
// (P20c; seeded VIR / REU).

function todayISO(offsetDays = 0) {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  return d.toISOString().slice(0, 10);
}
function toBelgian(iso: any) {
  if (!iso) return "—";
  const d = String(iso).slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) return iso;
  return `${d.slice(8, 10)}/${d.slice(5, 7)}/${d.slice(0, 4)}`;
}
function toBelgianDateTime(isoLike: any) {
  if (!isoLike) return "—";
  const s = String(isoLike);
  const datePart = s.slice(0, 10);
  const timePart = s.slice(11, 16);
  const beDate = toBelgian(datePart);
  return timePart ? `${beDate} ${timePart}` : beDate;
}

/* 164 §E (eslint ratchet) — the shapes this modal actually handles, typed so
   the new collective-case leg does not add unsafe-access debt. */
type PickerCase = {
  id: number;
  ref?: string;
  title?: string;
  status?: string;
  isProspect?: boolean;
};
type PickerOption = {
  key: string;
  kind: "case" | "cc";
  id: number;
  ref?: string;
  title?: string;
  suffix: string;
};
type OiRow = {
  outlookEventId: string;
  subject: string;
  start: string;
  end?: string;
  onlineMeetingUrl?: string | null;
  isOnlineMeeting?: boolean;
  classification: "NEW" | "ALREADY";
  suggestedCaseId?: number | null;
  suggestedCcId?: number | null;
  suggestedCcRef?: string | null;
  selected: boolean;
  caseId: number | null;
  ccId: number | null;
  activityType?: string;
};

type RowCasePickerProps = {
  value?: string | null;
  options: PickerOption[];
  disabled?: boolean;
  onPick: (o: PickerOption | null) => void;
};
// Per-row case picker with typeahead. Dropdown is rendered via portal
// (position:fixed, anchored to the trigger's captured rect) to escape
// the scrollable table wrapper's overflow:auto clipping.
function RowCasePicker({ value, options, disabled, onPick }: RowCasePickerProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [rect, setRect] = useState<any>(null);
  const triggerRef = useRef<any>(null);
  const inputRef = useRef<any>(null);

  // 164 §B.2 — options carry a composite `key` (ids collide across the
  // case / collective-case tables); `value` is that key, `onPick` hands
  // back the whole option (or null to clear).
  const selected = useMemo(
    () => (value ? options.find((o) => o.key === value) : null),
    [value, options],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => {
      const hay =
        `${o.ref || ""} ${o.title || ""} ${o.suffix || ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [options, query]);

  const close = () => {
    setOpen(false);
    setQuery("");
  };

  const openAt = () => {
    if (disabled) return;
    const r = triggerRef.current?.getBoundingClientRect();
    if (r) {
      /* 164 §B.1 — clamp to the window. The portal is position:fixed with
         maxHeight 320 and used to be clamped by nothing: a row near the
         bottom opened off-screen, and on an XL display the box could leave
         the right edge. If the box cannot fit below the trigger, open
         UPWARD (anchored by `bottom`, so it grows away from the trigger);
         clamp left so left + width ≤ innerWidth − 8. */
      const width = Math.max(r.width, 320);
      const left = Math.min(
        r.left,
        Math.max(8, window.innerWidth - 8 - width),
      );
      const openUp = r.bottom + 2 + 320 > window.innerHeight;
      setRect(
        openUp
          ? { left, bottom: window.innerHeight - r.top + 2, width: r.width }
          : { left, top: r.bottom + 2, width: r.width },
      );
    }
    setOpen(true);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  useEffect(() => {
    if (!open) return;
    const onResize = () => close();
    window.addEventListener("resize", onResize);
    // 164 §B.1 — a fixed box does not follow a scrolled anchor: scrolling
    // the table wrapper closes the picker, exactly as resize does above.
    const wrap = triggerRef.current?.closest?.(".table-wrap");
    wrap?.addEventListener("scroll", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      wrap?.removeEventListener("scroll", onResize);
    };
  }, [open]);

  const label = selected
    ? (selected.ref
        ? `${selected.ref} — ${selected.title || ""}`
        : selected.title || `#${selected.id}`) +
      (selected.suffix ? ` · ${selected.suffix}` : "")
    : t("outlookImport.select_case", { fallback: "— Select case —" });

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className="field-input"
        disabled={disabled}
        onClick={openAt}
        title={selected ? label : ""}
        style={{
          padding: "2px 6px",
          fontSize: FS.s11,
          width: "100%",
          textAlign: "left",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          color: selected ? "var(--tp)" : "var(--tm)",
          cursor: disabled ? "default" : "pointer",
          background: "var(--bi)",
        }}
      >
        {label}
      </button>
      {open &&
        rect &&
        createPortal(
          <>
            <div
              onClick={close}
              style={{ position: "fixed", inset: 0, zIndex: Z.portal }}
            />
            <div
              style={{
                position: "fixed",
                left: rect.left,
                // 164 §B.1 — anchored by top (downward) or bottom (upward).
                ...(rect.top != null
                  ? { top: rect.top }
                  : { bottom: rect.bottom }),
                width: Math.max(rect.width, 320),
                zIndex: Z.portalMenu,
                background: "var(--bs)",
                border: "1px solid var(--br)",
                borderRadius: RAD.r6,
                boxShadow: "0 8px 24px rgba(0,0,0,.4)",
                display: "flex",
                flexDirection: "column",
                maxHeight: 320,
              }}
            >
              <div style={{ padding: 6, borderBottom: "1px solid var(--br)" }}>
                <input
                  ref={inputRef}
                  className="field-input"
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={t("outlookImport.search_by_ref_or_title", {
                    fallback: "Search by ref or title…",
                  })}
                  style={{ padding: "4px 8px", fontSize: FS.s11, width: "100%" }}
                />
              </div>
              <div style={{ overflowY: "auto", flex: 1 }}>
                {filtered.length === 0 ? (
                  <div
                    style={{
                      padding: "8px 10px",
                      fontSize: FS.s11,
                      color: "var(--tm)",
                    }}
                  >
                    {t("outlookImport.no_cases_match", {
                      fallback: "No cases match.",
                    })}
                  </div>
                ) : (
                  filtered.map((c) => {
                    const isSel = c.key === value;
                    return (
                      <div
                        key={c.key}
                        onClick={() => {
                          onPick(c);
                          close();
                        }}
                        style={{
                          padding: "5px 10px",
                          fontSize: FS.s11,
                          cursor: "pointer",
                          background: isSel ? "var(--as)" : "transparent",
                          color: isSel ? "var(--ac)" : "var(--tp)",
                          borderBottom: "1px solid rgba(255,255,255,.04)",
                        }}
                        onMouseEnter={(e) => {
                          if (!isSel)
                            e.currentTarget.style.background = "var(--bh)";
                        }}
                        onMouseLeave={(e) => {
                          if (!isSel)
                            e.currentTarget.style.background = "transparent";
                        }}
                      >
                        {c.ref ? (
                          <span>
                            <span
                              style={{
                                fontFamily: "'JetBrains Mono', monospace",
                                fontSize: FS.s10_5,
                              }}
                            >
                              {c.ref}
                            </span>
                            {c.title ? (
                              <span style={{ color: "var(--tm)" }}>
                                {" "}
                                — {c.title}
                              </span>
                            ) : null}
                            {c.suffix ? (
                              <span
                                style={{
                                  color: "var(--tm)",
                                  fontStyle: "italic",
                                }}
                              >
                                {" · " + c.suffix}
                              </span>
                            ) : null}
                          </span>
                        ) : (
                          <span>
                            {c.title || `#${c.id}`}
                            {c.suffix ? (
                              <span
                                style={{
                                  color: "var(--tm)",
                                  fontStyle: "italic",
                                }}
                              >
                                {" · " + c.suffix}
                              </span>
                            ) : null}
                          </span>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
              {value && (
                <div style={{ padding: 6, borderTop: "1px solid var(--br)" }}>
                  <button
                    type="button"
                    className="btn btn-ghost"
                    onClick={() => {
                      onPick(null);
                      close();
                    }}
                    style={{ padding: "2px 8px", fontSize: FS.s10 }}
                  >
                    {t("outlookImport.clear_selection", {
                      fallback: "Clear selection",
                    })}
                  </button>
                </div>
              )}
            </div>
          </>,
          document.body,
        )}
    </>
  );
}

type OutlookImportModalProps = {
  onClose?: any;
  ca?: PickerCase[];
  // 164 §B.2 — the collective cases join the picker.
  cc?: PickerCase[];
  onImported?: any;
  toast?: any;
  nav?: any;
};

export default function OutlookImportModal({
  onClose,
  ca,
  cc,
  onImported,
  toast,
  nav,
}: OutlookImportModalProps) {
  const { t } = useTranslation();
  // P20c — which event types an imported meeting may land on is event_type
  // taxonomy data (meta.m365ImportRole), the SAME fold the server's import
  // route resolves through, so the picker can never offer a type the route
  // would reject. `virtual` / `inPerson` are seeded VIR / REU.
  const m365Types = m365ImportTypesFromEntries(
    useTaxonomy("event_type").entries,
  );
  const importTypes = [m365Types.virtual, m365Types.inPerson].filter(Boolean);
  const [from, setFrom] = useState(todayISO(-30));
  const [to, setTo] = useState(todayISO(90));
  const [hideAlready, setHideAlready] = useState(true);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [searched, setSearched] = useState(false);
  const [rows, setRows] = useState<OiRow[]>([]); // server rows + per-row UI state
  const [m365Missing, setM365Missing] = useState(false);
  const [errMsg, setErrMsg] = useState("");

  /* 164 §B.2 — the options are BOTH kinds. MEASURED: AgendaView receives
     `ca={cases}` raw (App.tsx, the AgView mount) — prospects and archived
     included — so the filter lives here: every NON-ARCHIVED case of both
     kinds is offered; a prospect is offered with its suffix. Ids collide
     across the two tables, so each option carries a composite `key`
     ("ca:5" / "cc:5") and the picker works on keys, never bare ids.
     Sorted alphabetically by title; no title sinks to the bottom. */
  const sortedCases = useMemo(() => {
    const suffixCc = t("outlookImport.kind_collective", {
      fallback: "collective",
    });
    const suffixProspect = t("outlookImport.kind_prospect", {
      fallback: "prospect",
    });
    const opts: PickerOption[] = [
      ...(ca || [])
        .filter((c) => c.status !== "archived")
        .map((c) => ({
          key: "ca:" + c.id,
          kind: "case" as const,
          id: c.id,
          ref: c.ref,
          title: c.title,
          suffix: c.isProspect ? suffixProspect : "",
        })),
      ...(cc || [])
        .filter((c) => c.status !== "archived")
        .map((c) => ({
          key: "cc:" + c.id,
          kind: "cc" as const,
          id: c.id,
          ref: c.ref,
          title: c.title,
          suffix: suffixCc,
        })),
    ];
    return opts.sort((a, b) => {
      const ta = (a.title || "").toLowerCase();
      const tb = (b.title || "").toLowerCase();
      if (!ta && !tb) return 0;
      if (!ta) return 1;
      if (!tb) return -1;
      return ta.localeCompare(tb);
    });
  }, [ca, cc, t]);

  const runSearch = async () => {
    setLoading(true);
    setSearched(false);
    setErrMsg("");
    setM365Missing(false);
    try {
      const fromIso = `${from}T00:00:00`;
      const toIso = `${to}T23:59:59`;
      // 174 §1.2 — through api(): BOTH credentials (the lx:token Bearer and
      // the lx_session cookie), not the cookie alone. api() supplies the /api
      // prefix, throws on a non-ok, and carries `status` and `code` from the
      // envelope onto the thrown Error — which is where the 409
      // "not connected" branch reads them now (see the catch).
      const url = `/agenda/outlook/search?from=${encodeURIComponent(fromIso)}&to=${encodeURIComponent(toIso)}`;
      const data = (await api.get(url)) as OiRow[];
      const ui = data.map((d) => ({
        ...d,
        // NEW rows are checked by default; ALREADY rows are not
        // selectable per the brief.
        selected: d.classification === "NEW",
        caseId: d.suggestedCaseId || null,
        // 164 §B.2 — the collective-case suggestion PRE-FILLS the picker
        // now that the import can land on one.
        ccId: d.suggestedCaseId ? null : d.suggestedCcId || null,
        // Auto-classify per row from Graph's isOnlineMeeting field, onto
        // whichever event type the taxonomy tags for that role (seeded:
        // online → VIR, physical → REU). User can still override via the
        // per-row toggle.
        activityType: d.isOnlineMeeting
          ? m365Types.virtual
          : m365Types.inPerson,
      }));
      setRows(ui);
      setSearched(true);
    } catch (e: any) {
      /* 174 §1.2 — AND THE BRANCH WAS DEAD BEFORE THIS COMMIT. The check read
         `j?.code === "M365_NOT_CONNECTED"`; `agenda.js:1467` answers
         `code: 'm365.not_connected'`. No 409 ever matched, so "connect M365"
         rendered as a red error string instead of the pane that offers the
         Settings link. Corrected to the code the server actually sends. */
      if (e?.status === 409 && e?.code === "m365.not_connected") {
        setM365Missing(true);
        setRows([]);
        setSearched(true);
        return;
      }
      setErrMsg(
        e.message ||
          t("outlookImport.search_failed", { fallback: "Search failed" }),
      );
    } finally {
      setLoading(false);
    }
  };

  const visibleRows = hideAlready
    ? rows.filter((r) => r.classification === "NEW")
    : rows;
  const selectedRows = rows.filter(
    (r) => r.selected && r.classification === "NEW",
  );
  // 164 §B.2 — a row is landable on either kind; blocking = neither.
  const blockingMissingCase = selectedRows.some(
    (r) => !r.caseId && !r.ccId,
  );
  const importLabel = t("outlookImport.import_n_selected", {
    count: selectedRows.length,
    fallback: `Import ${selectedRows.length} selected`,
  });

  const updateRow = (id: string, patch: Partial<OiRow>) => {
    setRows((prev) =>
      prev.map((r) => (r.outlookEventId === id ? { ...r, ...patch } : r)),
    );
  };

  const runImport = async () => {
    if (!selectedRows.length || blockingMissingCase) return;
    setImporting(true);
    setErrMsg("");
    try {
      const items = selectedRows.map((r) => ({
        outlookEventId: r.outlookEventId,
        subject: r.subject,
        start: r.start,
        // 164 §B.2 — exactly one of the two (the route refuses both/neither).
        ...(r.ccId ? { ccId: r.ccId } : { caseId: r.caseId }),
        activityType: r.activityType,
        // Phase 31 (C4) — the join URL is persisted on import now (agenda
        // rows with one get a Join affordance; alerts carry it too).
        onlineMeetingUrl: r.onlineMeetingUrl || null,
      }));
      // 174 §1.2 — through api(): BOTH credentials, plus the X-LexCRM a
      // cookie-auth mutation needs. api() supplies the /api prefix.
      const j = (await api.post("/agenda/outlook/import", { items })) as {
        imported: number;
        skipped: number;
      };
      toast(
        t("outlookImport.imported_summary", {
          imported: j.imported,
          skipped: j.skipped,
          fallback: `Imported ${j.imported}, skipped ${j.skipped}`,
        }),
        /* 166 §G.3 — this said "Imported 0, skipped 12" in green. MEASURED
           what `skipped` means before choosing the slot: `agenda.js:1761`
           returns `items.length - result.count` off a `createMany({
           skipDuplicates: true })`, so a skipped row is a DUPLICATE, not a
           failure — `duplicate`, which is warning when some were skipped and
           error only when nothing landed at all. */
        outcomeVariant({ ok: j.imported, duplicate: j.skipped, failed: 0 }),
      );
      if (onImported) await onImported();
      onClose();
    } catch (e: any) {
      setErrMsg(
        e.message ||
          t("outlookImport.import_failed", { fallback: "Import failed" }),
      );
    } finally {
      setImporting(false);
    }
  };

  const goSettings = () => {
    onClose();
    nav("settings");
  };

  const inputStyle = { padding: "4px 8px", fontSize: FS.s12, minWidth: 140 };

  // ModalLayout with `rawBody` (188 §A) so the results table can own the
  // scroll inside a viewport-bounded modal: header section (date pickers,
  // search) and footer (Cancel, Import) stay fixed while only the table area
  // scrolls. Until 188 this was a hand-rolled OV/ML structure "(not
  // ModalLayout)" for that reason; rawBody keeps the body the caller's, so
  // the reason no longer needs a second shell. Pattern mirrors OneDrivePicker.
  return (
    <ModalLayout
      stopShellClicks
      /* 166 §C(2) — THE PIXEL FIGURE IS THE WIDTH. This read
         `maxWidth: 1040, width: "94vw"`, and the maxWidth was inert
         (`.modal`'s `max-width: … !important` outranks it, App.css:1057),
         so what actually rendered was 94% of the window — 1880px on
         Laurent's 2000px screen, hard against both edges. Laurent's ruling,
         2026-09-04: no modal is bound to the window's width. The 1040 its
         author named becomes the width; the `!important`
         `min(92vw, …)` is the small-screen guard that keeps it inside a
         narrow viewport, which is the only job a viewport figure has here. */
      width={1040}
      maxHeight="85vh"
      rawBody
      headerContent={
        <div className="modal-header">
          <h3>
            {t("outlookImport.title", { fallback: "Import from Outlook" })}
          </h3>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={onClose}
            style={{ padding: "2px 6px" }}
          >
            <X size={14} />
          </button>
        </div>
      }
      footer={
        <div
          className="modal-footer"
          style={{ flexDirection: "row", justifyContent: "flex-end", gap: 8 }}
        >
          <button
            type="button"
            className="btn btn-ghost"
            onClick={onClose}
            style={{ padding: "5px 12px" }}
          >
            {t("common.cancel", { fallback: "Cancel" })}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={runImport}
            disabled={importing || !selectedRows.length || blockingMissingCase}
            style={{
              padding: "5px 14px",
              opacity:
                importing || !selectedRows.length || blockingMissingCase
                  ? 0.5
                  : 1,
            }}
            title={
              blockingMissingCase
                ? t("outlookImport.needs_case_title", {
                    fallback: "Every selected row needs a case before import",
                  })
                : ""
            }
          >
            {importing
              ? t("outlookImport.importing", { fallback: "Importing…" })
              : importLabel}
          </button>
        </div>
      }
    >

        <div
          style={{
            padding: "10px 16px",
            borderBottom: "1px solid var(--br)",
            display: "flex",
            flexWrap: "wrap",
            alignItems: "end",
            gap: 10,
          }}
        >
          <div className="field" style={{ margin: 0 }}>
            <label style={{ fontSize: FS.s10, color: "var(--tm)" }}>
              {t("emailSearch.from", { fallback: "From" })}
            </label>
            <input
              className="field-input"
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              style={inputStyle}
            />
            <span style={{ fontSize: FS.s9_5, color: "var(--tm)" }}>
              {toBelgian(from)}
            </span>
          </div>
          <div className="field" style={{ margin: 0 }}>
            <label style={{ fontSize: FS.s10, color: "var(--tm)" }}>
              {t("emailSearch.to", { fallback: "To" })}
            </label>
            <input
              className="field-input"
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              style={inputStyle}
            />
            <span style={{ fontSize: FS.s9_5, color: "var(--tm)" }}>
              {toBelgian(to)}
            </span>
          </div>
          <button
            type="button"
            className="btn btn-primary"
            onClick={runSearch}
            disabled={loading || !from || !to || from > to}
            style={{ padding: "5px 14px" }}
          >
            {loading
              ? t("emailSearch.searching", { fallback: "Searching…" })
              : t("emailSearch.search", { fallback: "Search" })}
          </button>
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              fontSize: FS.s11,
              marginLeft: "auto",
              cursor: "pointer",
            }}
          >
            <input
              type="checkbox"
              checked={hideAlready}
              onChange={(e) => setHideAlready(e.target.checked)}
            />
            {t("outlookImport.only_new", {
              fallback: "Only events not already in LexCRM",
            })}
          </label>
        </div>

        <div
          className="modal-body"
          style={{
            padding: "10px 16px",
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
          }}
        >
          {errMsg && (
            <div
              style={{
                background: "rgba(248,113,113,.1)",
                color: "var(--rd)",
                padding: "6px 10px",
                borderRadius: RAD.r6,
                fontSize: FS.s11,
                marginBottom: 8,
              }}
            >
              {errMsg}
            </div>
          )}

          {m365Missing && (
            <div
              style={{
                background: "rgba(74,124,255,.1)",
                padding: "10px 12px",
                borderRadius: RAD.r6,
                fontSize: FS.s11_5,
                marginBottom: 8,
              }}
            >
              {t("outlookImport.m365_missing_lead", {
                fallback: "Microsoft 365 is not connected for your account.",
              })}{" "}
              <button
                type="button"
                className="link"
                onClick={goSettings}
                style={{
                  background: "none",
                  border: "none",
                  padding: 0,
                  color: "var(--ac)",
                  cursor: "pointer",
                  textDecoration: "underline",
                }}
              >
                {t("outlookImport.m365_open_settings", {
                  fallback: "Open Settings → M365 connection",
                })}
              </button>{" "}
              {t("outlookImport.m365_and_try_again", {
                fallback: "and try again.",
              })}
            </div>
          )}

          {searched && !m365Missing && rows.length === 0 && (
            <div
              style={{ padding: "12px 0", color: "var(--tm)", fontSize: FS.s11_5 }}
            >
              {t("outlookImport.no_events", {
                fallback: "No events found in this date range.",
              })}
            </div>
          )}

          {visibleRows.length > 0 && (
            <div
              className="table-wrap"
              style={{
                flex: 1,
                minHeight: 0,
                overflow: "auto",
                border: "1px solid var(--br)",
                borderRadius: RAD.r6,
              }}
            >
              <Table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: FS.s11_5,
                }}
              >
                <thead
                  style={{
                    position: "sticky",
                    top: 0,
                    background: "var(--bs)",
                    zIndex: Z.stickyHead,
                  }}
                >
                  <tr style={{ borderBottom: "1px solid var(--br)" }}>
                    <th
                      style={{
                        padding: "6px 8px",
                        textAlign: "left",
                        width: 28,
                      }}
                    >
                      {/* 164 §B.3 — select all VISIBLE NEW rows; ALREADY
                          rows are never touched. Indeterminate when some
                          but not all are selected. */}
                      <input
                        type="checkbox"
                        aria-label={t("outlookImport.select_all", {
                          fallback: "Select all",
                        })}
                        ref={(el) => {
                          if (!el) return;
                          const vis = visibleRows.filter(
                            (r) => r.classification === "NEW",
                          );
                          const sel = vis.filter((r) => r.selected);
                          el.indeterminate =
                            sel.length > 0 && sel.length < vis.length;
                        }}
                        checked={(() => {
                          const vis = visibleRows.filter(
                            (r) => r.classification === "NEW",
                          );
                          return (
                            vis.length > 0 &&
                            vis.every((r) => r.selected)
                          );
                        })()}
                        onChange={(e) => {
                          const on = e.target.checked;
                          const visIds = new Set(
                            visibleRows
                              .filter(
                                (r) => r.classification === "NEW",
                              )
                              .map((r) => r.outlookEventId),
                          );
                          setRows((prev) =>
                            prev.map((r) =>
                              visIds.has(r.outlookEventId)
                                ? { ...r, selected: on }
                                : r,
                            ),
                          );
                        }}
                      />
                    </th>
                    <th
                      style={{
                        padding: "6px 8px",
                        textAlign: "left",
                        width: 130,
                      }}
                    >
                      {t("outlookImport.col_datetime", {
                        fallback: "Date / time",
                      })}
                    </th>
                    <th style={{ padding: "6px 8px", textAlign: "left" }}>
                      {t("outlookImport.col_title", { fallback: "Title" })}
                    </th>
                    <th
                      style={{
                        padding: "6px 8px",
                        textAlign: "left",
                        width: 360,
                      }}
                    >
                      {t("outlookImport.col_case", { fallback: "Case" })}
                    </th>
                    <th
                      style={{
                        padding: "6px 8px",
                        textAlign: "left",
                        width: 110,
                      }}
                    >
                      {t("outlookImport.col_activity", {
                        fallback: "Activity",
                      })}
                    </th>
                    <th
                      style={{
                        padding: "6px 8px",
                        textAlign: "left",
                        width: 70,
                      }}
                    >
                      {t("outlookImport.col_status", { fallback: "Status" })}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.map((r) => {
                    const isAlready = r.classification === "ALREADY";
                    const rowStyle = {
                      borderBottom: "1px solid rgba(255,255,255,.04)",
                      opacity: isAlready ? 0.45 : 1,
                    };
                    return (
                      <tr key={r.outlookEventId} style={rowStyle}>
                        <td style={{ padding: "5px 8px" }}>
                          <input
                            type="checkbox"
                            checked={!isAlready && r.selected}
                            disabled={isAlready}
                            onChange={(e) =>
                              updateRow(r.outlookEventId, {
                                selected: e.target.checked,
                              })
                            }
                          />
                        </td>
                        <td
                          style={{
                            padding: "5px 8px",
                            fontFamily: "'JetBrains Mono', monospace",
                            fontSize: FS.s10_5,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {toBelgianDateTime(r.start)}
                        </td>
                        <td style={{ padding: "5px 8px" }} title={r.subject}>
                          <div
                            style={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              maxWidth: 320,
                            }}
                          >
                            {r.subject ||
                              t("email.no_subject", { // 209 §6 — the one word for an e-mail with no subject
                                fallback: "(no subject)",
                              })}
                          </div>
                        </td>
                        <td style={{ padding: "5px 8px" }}>
                          <RowCasePicker
                            // 164 §B.2 — the row holds exactly one of the
                            // two; the picker speaks composite keys.
                            value={
                              r.caseId
                                ? "ca:" + r.caseId
                                : r.ccId
                                  ? "cc:" + r.ccId
                                  : null
                            }
                            options={sortedCases}
                            disabled={isAlready}
                            onPick={(o) =>
                              updateRow(
                                r.outlookEventId,
                                !o
                                  ? { caseId: null, ccId: null }
                                  : o.kind === "cc"
                                    ? { ccId: o.id, caseId: null }
                                    : { caseId: o.id, ccId: null },
                              )
                            }
                          />
                          {/* 164 §B.2 — the collective leg landed: a subject
                              naming a collective case now arrives PRE-FILLED
                              (suggestedCcId). The hint survives only for a
                              row where nothing at all was found. */}
                          {r.suggestedCcRef && !r.caseId && !r.ccId && (
                            <div
                              style={{
                                marginTop: 3,
                                fontSize: FS.s9_5,
                                color: "var(--tm)",
                              }}
                            >
                              {t("outlookImport.names_collective_case", {
                                ref: r.suggestedCcRef,
                                fallback: `Subject names collective case ${r.suggestedCcRef} — pick the case to import onto`,
                              })}
                            </div>
                          )}
                        </td>
                        <td style={{ padding: "5px 8px" }}>
                          <div
                            style={{
                              display: "inline-flex",
                              border: "1px solid var(--br)",
                              borderRadius: RAD.r4,
                              overflow: "hidden",
                            }}
                          >
                            {importTypes.map((t) => (
                              <button
                                key={t}
                                type="button"
                                disabled={isAlready}
                                onClick={() =>
                                  updateRow(r.outlookEventId, {
                                    activityType: t,
                                  })
                                }
                                style={{
                                  padding: "2px 9px",
                                  fontSize: FS.s10,
                                  fontFamily: "'JetBrains Mono', monospace",
                                  fontWeight: 600,
                                  border: "none",
                                  background:
                                    r.activityType === t
                                      ? "var(--as)"
                                      : "var(--bi)",
                                  color:
                                    r.activityType === t
                                      ? "var(--ac)"
                                      : "var(--tm)",
                                  cursor: isAlready ? "default" : "pointer",
                                }}
                              >
                                {t}
                              </button>
                            ))}
                          </div>
                        </td>
                        <td style={{ padding: "5px 8px", fontSize: FS.s10 }}>
                          <span
                            style={{
                              padding: "1px 6px",
                              borderRadius: RAD.r8,
                              background: isAlready
                                ? "rgba(255,255,255,.06)"
                                : "rgba(74,222,128,.15)",
                              color: isAlready ? "var(--tm)" : "var(--gn)",
                              fontWeight: 600,
                            }}
                          >
                            {r.classification}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          )}

          {blockingMissingCase && (
            <div style={{ marginTop: 8, fontSize: FS.s10_5, color: "var(--am)" }}>
              {t("outlookImport.needs_case_warning", {
                fallback: "⚠ Every selected row needs a case before import.",
              })}
            </div>
          )}
        </div>

    </ModalLayout>
  );
}
